// Черновики ввода таблицы протокола в браузере (011, research Р11; уточнение 22.10.2026): текст
// ячейки хранится, пока сервер не принял правку. Ключ — «пользователь + протокол + пункт + поле»
// (security CHK017); черновик живёт до сохранения, выхода пользователя или 7 дней (ux CHK015).
// Хранилище может быть недоступно (частный режим) — тогда черновиков нет, правка идёт как обычно.

const PREFIX = 'kp.minutes.draft:';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  value: string;
  at: number;
}

export interface Draft {
  itemId: string;
  field: string;
  value: string;
  at: number;
}

export interface DraftStore {
  save(itemId: string, field: string, value: string): void;
  read(itemId: string, field: string): string | null;
  drop(itemId: string, field: string): void;
  /** Черновики этого протокола этого пользователя. */
  list(): Draft[];
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function parse(raw: string | null): StoredDraft | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      value !== null &&
      typeof value === 'object' &&
      'value' in value &&
      typeof value.value === 'string' &&
      'at' in value &&
      typeof value.at === 'number'
    ) {
      return { value: value.value, at: value.at };
    }
  } catch {
    // Испорченная запись — как нет черновика.
  }
  return null;
}

function keysOf(store: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < store.length; index += 1) {
    const key = store.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

export function createDraftStore(
  userId: string,
  documentId: string,
  now: () => number = Date.now,
): DraftStore {
  const base = `${PREFIX}${userId}:${documentId}:`;
  const key = (itemId: string, field: string) => `${base}${itemId}:${field}`;
  return {
    save(itemId, field, value) {
      try {
        storage()?.setItem(key(itemId, field), JSON.stringify({ value, at: now() }));
      } catch {
        // Хранилище переполнено или запрещено: правка уйдёт на сервер и без черновика.
      }
    },
    read(itemId, field) {
      return parse(storage()?.getItem(key(itemId, field)) ?? null)?.value ?? null;
    },
    drop(itemId, field) {
      try {
        storage()?.removeItem(key(itemId, field));
      } catch {
        // Нечего убирать.
      }
    },
    list() {
      const store = storage();
      if (!store) return [];
      return keysOf(store)
        .filter((name) => name.startsWith(base))
        .flatMap((name) => {
          const draft = parse(store.getItem(name));
          const rest = name.slice(base.length);
          const split = rest.indexOf(':');
          if (!draft || split < 0) return [];
          return [{ itemId: rest.slice(0, split), field: rest.slice(split + 1), ...draft }];
        });
    },
  };
}

/** Убрать черновики старше 7 дней. */
export function purgeOldDrafts(now: number = Date.now()): void {
  const store = storage();
  if (!store) return;
  try {
    for (const name of keysOf(store)) {
      const draft = parse(store.getItem(name));
      if (!draft || now - draft.at > TTL_MS) store.removeItem(name);
    }
  } catch {
    // Не вышло — уберём в следующий раз.
  }
}

/** Выход из сеанса: черновики таблицы протокола стираются (security CHK017). */
export function dropAllDrafts(): void {
  const store = storage();
  if (!store) return;
  try {
    for (const name of keysOf(store)) store.removeItem(name);
  } catch {
    // Хранилище недоступно — и черновиков в нём нет.
  }
}
